import { Component, Fragment, useEffect } from 'react';
import type { ReactNode } from 'react';

import { useRouter } from 'next/router';

import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConnectedRouter } from 'connected-react-router';
import { object } from 'prop-types';
import { connect, Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from 'styled-components';

import { ClientContext } from 'src/app/client';
import { DynamicPageTitle } from 'src/app/components/Elements/DynamicPageTitle/container';
import LoadCapsuleFacility from 'src/app/components/Elements/LoadCapsuleFacility/container';
import Location from 'src/app/components/Elements/Location/container';
import Modal from 'src/app/components/Elements/Modal/container';
import { ErrorAlert } from 'src/app/components/Error/Alert/container';
import { ErrorBoundary } from 'src/app/components/Error/Boundary';
import AmplifyContextProvider from 'src/app/context/AuthContext';
import ExperimentsContextProvider from 'src/app/context/ExperimentsContext';
import { LayoutManager } from 'src/app/popsicle/router/layoutManager';
import InsuranceStateProvider from 'src/app/providers/InsuranceStateProvider';
import MedicationCardStateProvider from 'src/app/providers/MedicationCardStateProvider';
import HISTORY from 'src/app/router/history';
import { client, persistor, store } from 'src/app/state/store';
import { theme } from 'src/styles/theme';

class Client extends Component<{ children?: ReactNode; client?: any }> {
  static propTypes = { client: object.isRequired };
  static childContextTypes = { client: object };
  getChildContext() {
    return {
      client: this.props.client,
    };
  }

  render() {
    return <Fragment>{this.props.children}</Fragment>;
  }
}

const mapStateToProps = (state: any) => ({ client: state.client });

const ClientProvider = connect(mapStateToProps)(Client);

const queryClient = new QueryClient();

const App = () => {
  const { asPath } = useRouter();

  useEffect(() => {
    if (navigator?.storage && navigator?.storage.estimate) {
      navigator.storage
        .estimate()
        .then(estimate => {
          console.log({ estimate });
          if (estimate.usage && estimate.quota) {
            console.log(`Quota: ${estimate.quota}, Usage: ${estimate.usage}`);
            const usagePercentage = (estimate.usage / estimate.quota) * 100;
            console.log(`You've used ${usagePercentage.toFixed(2)}% of your storage quota.`);
          }
        })
        .catch(error => {
          console.error('Error estimating storage:', error);
        });
    }
  }, []);

  /**
   * We force the user to be logged out when they enter the password reset flow; however, the render cycle sometimes
   * allows authenticated requests to be called if a user used an old tab or logged in after beginning the password
   * reset flow. Checking the router will allow us to prevent making unnecessary API calls.
   */
  const shouldIgnoreAuthenticatedFetches = asPath?.startsWith('/auth/reset_password');

  return (
    <ReduxProvider store={store}>
      <ErrorBoundary>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <AmplifyContextProvider>
                <ExperimentsContextProvider>
                  <MedicationCardStateProvider>
                    <InsuranceStateProvider>
                      <ClientProvider>
                        <ClientContext.Provider value={client}>
                          <ConnectedRouter history={HISTORY}>
                            <DynamicPageTitle />
                            <Location />
                            <Modal />
                            {!shouldIgnoreAuthenticatedFetches && <LoadCapsuleFacility />}
                            <LayoutManager />
                            <ErrorAlert />
                          </ConnectedRouter>
                        </ClientContext.Provider>
                      </ClientProvider>
                    </InsuranceStateProvider>
                  </MedicationCardStateProvider>
                </ExperimentsContextProvider>
              </AmplifyContextProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </PersistGate>
      </ErrorBoundary>
    </ReduxProvider>
  );
};

export default Sentry.withProfiler(App);
